import React from 'react';
import { categoryGrade, categoryTally, fixFor, whyFor, plural } from './shapeReport';
import ReportLogo from './ReportLogo';
import StrengthsGaps from './StrengthsGaps';

// Single-page "PDF / document" report. Same data + visual language as the slide deck, but a
// continuous scrolling page (no slides, no scroll-snap) so it reads top-to-bottom and prints
// cleanly via the browser's Save-as-PDF. Reuses the deck's `.deck-*` visual atoms inside
// `.report-*` section wrappers.

const sevPill = (severity) => (
  <span className={`deck-pill sev-${severity || 'info'}`}>{(severity || 'info').toUpperCase()}</span>
);

const HEADER_KEYWORDS = [
  'What we tested', 'Test Results', 'Analysis', 'Risk Assessment', 'Security Risks',
  'Why this matters', 'Key Findings', 'Security Benefits', 'Recommended Actions',
  'Recommendations', 'Compliance', 'Compliance Implications', 'Impact Assessment',
  'Threat Analysis', 'Attack Vector Analysis', 'Security Considerations', 'Detailed Findings',
  'Policy Analysis', 'Security Implications', 'What makes these domains threats',
  'Defensive Recommendations', 'Threat Intelligence', 'What are parked/registration pages',
  'Why this is good', 'Why this is excellent', 'Still Recommended Actions', 'Best Practices',
  'Immediate Recommended Actions', 'Medium-Term Actions', 'Long-Term Strategy',
  'Additional Recommendations', 'Implementation Steps', 'Verification', 'Immediate Fix',
  'Alternative Approaches', 'Priority', 'Security Assessment', 'Critical Risks',
  'Specific Threat Analysis', 'Infrastructure Detection', 'Content Analysis', 'Threat Assessment',
  'Security Analysis', "What's wrong", 'Why this is a concern', 'Why this is a critical failure',
  'Why this is a significant security gap', 'What this means', 'Important Note', 'Analysis by Algorithm'
];

// Parse a finding's detail text into headers / bullets / label-value / text lines.
// Mirrors the deck's detail rendering so both layouts read identically.
function renderDetail(detail) {
  const fmt = (text) => text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/🔒/g, '<span class="detail-lock-icon">🔒</span>');
  return detail.split('\n').map((line, i) => {
    if (line.trim() === '') return <div key={i} className="detail-spacer" />;
    const trimmed = line.trim();
    // A keyword on its own line is a section header. With a sentence after the colon it
    // is a label and its text — styled as a header, the whole paragraph rendered bold.
    const headerKey = HEADER_KEYWORDS.find(k => trimmed.startsWith(k + ':'));
    if (headerKey && trimmed.length === headerKey.length + 1) {
      return <div key={i} className="detail-header" dangerouslySetInnerHTML={{ __html: fmt(line) }} />;
    }
    // The bullet dot comes from CSS, so the source's own "- " is dropped; a numbered step
    // keeps its number and gets no dot. Both were shown before: "• - item", "• 1. step".
    const numbered = trimmed.match(/^(\d+)\.\s+(.*)$/);
    if (numbered) {
      return <div key={i} className="detail-bullet detail-numbered" data-n={`${numbered[1]}.`} dangerouslySetInnerHTML={{ __html: fmt(numbered[2]) }} />;
    }
    if (trimmed.match(/^[-•*]\s/)) {
      return <div key={i} className="detail-bullet" dangerouslySetInnerHTML={{ __html: fmt(trimmed.replace(/^[-•*]\s+/, '')) }} />;
    }
    if (line.includes(':') && !line.match(/^https?:\/\//)) {
      const parts = line.split(':');
      if (parts.length >= 2 && parts[0].trim().length < 50) {
        return (
          <div key={i} className="detail-label-line">
            <span className="detail-label">{parts[0].trim()}:</span>
            <span className="detail-label-value" dangerouslySetInnerHTML={{ __html: fmt(parts.slice(1).join(':').trim()) }} />
          </div>
        );
      }
    }
    return <div key={i} className="detail-text-line" dangerouslySetInnerHTML={{ __html: fmt(line) }} />;
  });
}

function ReportPage({ rootRef, shaped, provider, gated, leadCollected, onUnlock, onDownload, version }) {
  const {
    data, domain, providerName, categories, totalFindings, reportTitle, reportLede,
    criticalCount, highCount, mediumCount, lowCount, riskLevel, grade, categoryCount,
    issueRows, impactCards, challengeRows, coverage,
    strengths, gaps, passCount, issueCount
  } = shaped;

  const locked = gated && !leadCollected;
  const gaugeAngle = (Math.max(0, Math.min(100, data.riskScore)) / 100) * 180 - 90;

  return (
    <div className="results-page report-page" ref={rootRef}>
      <div className="report-doc">

        {/* Hero */}
        <section className="report-section report-hero">
          <ReportLogo provider={provider} />
          {providerName && <div className="deck-presenter-name">{providerName}</div>}
          <div className="deck-title-divider"></div>
          <h1 className="deck-main-title report-hero-title">{domain}</h1>
          <p className="deck-subtitle">{reportTitle}</p>
          <p className="deck-presented-by">
            {providerName ? `Presented by ${providerName}` : 'Powered by Pipsqueak NetScan'}
          </p>
          <p className="deck-meta">
            🌐 {domain} · Scanned {new Date().toLocaleDateString()} · {plural(categoryCount, 'category', 'categories')} · {plural(totalFindings, 'check')}
          </p>
        </section>

        {/* Executive Risk Snapshot */}
        <section className="report-section">
          <div className="deck-eyebrow">📊 EXTERNAL SCAN RESULTS</div>
          <h2 className="deck-heading">Executive Risk Snapshot</h2>
          {/* Score and the 2x2 severity tiles side by side, exposure full width beneath —
              so a report with one category does not leave a tall empty panel beside them. */}
          <div className="deck-snapshot-stack">
            <div className="deck-panel deck-gauge-card">
              <div className="deck-panel-label">SECURITY SCORE</div>
              <svg viewBox="0 0 200 118" className="deck-gauge" aria-hidden="true">
                <defs>
                  <linearGradient id="reportGaugeGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#ef4444" />
                    <stop offset="55%" stopColor="#f59e0b" />
                    <stop offset="100%" stopColor="#10b981" />
                  </linearGradient>
                </defs>
                <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none"
                  stroke="url(#reportGaugeGrad)" strokeWidth="14" strokeLinecap="round" />
                <g transform={`rotate(${gaugeAngle} 100 100)`}>
                  <line x1="100" y1="100" x2="100" y2="36" stroke="var(--text)" strokeWidth="3" strokeLinecap="round" />
                  <circle cx="100" cy="100" r="6" fill="var(--text)" />
                </g>
                <text x="20" y="115" className="deck-gauge-tick">0</text>
                <text x="180" y="115" textAnchor="end" className="deck-gauge-tick">100</text>
              </svg>
              <div className={`deck-gauge-value ${riskLevel}`}>{data.riskScore}<span>/100</span></div>
              <div className={`risk-badge ${riskLevel}`}>{riskLevel.toUpperCase()} RISK</div>
            </div>
            <div className="deck-sev-tiles">
              <div className="deck-panel deck-sev-tile"><div className="deck-sev-num sev-critical-text">{criticalCount}</div><div className="deck-sev-name">CRITICAL</div></div>
              <div className="deck-panel deck-sev-tile"><div className="deck-sev-num sev-high-text">{highCount}</div><div className="deck-sev-name">HIGH</div></div>
              <div className="deck-panel deck-sev-tile"><div className="deck-sev-num sev-medium-text">{mediumCount}</div><div className="deck-sev-name">MEDIUM</div></div>
              <div className="deck-panel deck-sev-tile"><div className="deck-sev-num sev-low-text">{lowCount}</div><div className="deck-sev-name">LOW</div></div>
            </div>
            <div className="deck-panel deck-exposure-card">
              <div className="deck-panel-label">EXTERNAL SURFACE EXPOSURE</div>
              <div className="deck-exposure-list">
                {/* A scan that finds nothing is a result, not a failure — but rendered as
                    an empty box it reads as one. Say what was checked and that it came back clean. */}
                {categories.length === 0 && (
                  <div className="deck-exposure-empty">
                    <strong>Nothing to report.</strong>
                    <span>
                      {reportLede
                        ? `${reportLede} Everything checked came back clean.`
                        : 'Every check we ran came back clean.'}
                    </span>
                  </div>
                )}
                {categories.map((cat, i) => {
                  const g = categoryGrade(cat);
                  return (
                    <div key={i} className="deck-exposure-row">
                      <span className={`deck-grade grade-${g}`}>{g}</span>
                      <span className="deck-exposure-name">{cat.name}</span>
                      <span className="deck-exposure-count">{categoryTally(cat)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className={`deck-callout ${riskLevel}`}>
            {criticalCount + highCount > 0
              ? <>A <strong>{grade}</strong> grade with <strong>{criticalCount + highCount} critical/high finding{criticalCount + highCount === 1 ? '' : 's'}</strong> means attackers have a working path in — these gaps are visible to anyone who looks.</>
              : issueCount > 0
                ? <>A <strong>{grade}</strong> grade with no critical or high findings — {plural(passCount, 'check')} passed, and {plural(issueCount, 'smaller item')} below {issueCount === 1 ? 'is a hardening opportunity' : 'are hardening opportunities'}.</>
                : <>A <strong>{grade}</strong> grade — all {plural(passCount, 'check')} passed. Here is what we verified.</>}
          </div>
        </section>

        {/* Strengths & gaps — the good and the bad, in full, before the gate. */}
        <section className="report-section">
          <div className="deck-eyebrow">⚖️ STRENGTHS &amp; GAPS</div>
          <h2 className="deck-heading">What Needs Attention <span className="deck-heading-accent">&amp; What's Working</span></h2>
          <StrengthsGaps strengths={strengths} gaps={gaps} />
        </section>

        {/* Unlock gate — everything below requires lead capture */}
        {locked && (
          <section className="report-section report-unlock">
            <div className="report-unlock-card">
              <div className="report-unlock-icon">🔒</div>
              <h3>Unlock the Full Report</h3>
              <p>You've seen the executive snapshot. Enter your details to reveal every finding, the business-impact breakdown, and the recommended fixes.</p>
              <button className="report-unlock-btn" onClick={onUnlock}>Unlock Full Report</button>
            </div>
          </section>
        )}

        {/* Gated body */}
        <div className={locked ? 'report-locked' : ''} aria-hidden={locked ? 'true' : undefined}>

          {/* High-risk findings */}
          <section className="report-section">
            <div className="deck-eyebrow">🚩 FINDINGS DETAIL</div>
            <h2 className="deck-heading">High-Risk Areas <span className="deck-heading-accent">Identified</span></h2>
            {issueRows.length === 0 ? (
              <div className="deck-panel deck-clean-panel">
                <strong>No high-risk areas identified.</strong>
                <span>Every check in this report passed. The strengths above are what keeps {domain} in good shape.</span>
              </div>
            ) : (
              <div className="deck-panel deck-table-panel">
                <table className="deck-table">
                  <thead><tr><th>Severity</th><th>Finding</th><th>Category</th></tr></thead>
                  <tbody>
                    {issueRows.map((f, i) => (
                      <tr key={i}>
                        <td>{sevPill(f.severity)}</td>
                        <td>
                          <div className="deck-table-title">{f.title}</div>
                          {f.description && <div className="deck-table-desc">{f.description}</div>}
                        </td>
                        <td className="deck-table-cat">{f.categoryName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Business risk translation */}
          {impactCards.length > 0 && (
            <section className="report-section">
              <div className="deck-eyebrow">📉 BUSINESS RISK TRANSLATION</div>
              <h2 className="deck-heading">Operational Impact for <span className="deck-heading-accent">{domain}</span></h2>
              <div className="deck-impact-grid">
                {impactCards.map((c, i) => (
                  <div key={i} className="deck-panel deck-impact-card">
                    <div className="deck-impact-icon">{c.icon}</div>
                    <h4>{c.head}</h4>
                    <p>{c.body}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Recommended fixes */}
          {challengeRows.length > 0 && (
            <section className="report-section">
              <div className="deck-eyebrow">🧭 AT A GLANCE</div>
              <h2 className="deck-heading">Findings <span className="deck-heading-accent">→ Recommended Fixes</span></h2>
              <div className="deck-panel deck-table-panel">
                <table className="deck-table deck-fix-table">
                  <thead><tr><th>Challenge</th><th>Recommended Fix</th><th>Why It Matters</th></tr></thead>
                  <tbody>
                    {challengeRows.map((f, i) => (
                      <tr key={i}>
                        <td><div className="deck-table-title">{f.title}</div><div className="deck-table-desc">{sevPill(f.severity)}</div></td>
                        <td className="deck-fix-cell">{fixFor(f)}</td>
                        <td className="deck-fix-cell">{whyFor(f)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Managed coverage.
              Only rendered when an RMM connector had something to compare against. This
              is the section a client acts on fastest: every other finding is about how a
              host is configured, this one is about a host nobody is looking after. */}
          {coverage && (
            <section className="report-section">
              <div className="deck-eyebrow">🖥️ MANAGED COVERAGE</div>
              {coverage.total ? (
                <h2 className="deck-heading">
                  {coverage.managed} of {coverage.total}{" "}
                  <span className="deck-heading-accent">self-hosted systems under management</span>
                </h2>
              ) : (
                <h2 className="deck-heading">
                  Hosted by <span className="deck-heading-accent">third-party providers</span>
                </h2>
              )}
              <div className="deck-panel">
                {!coverage.total ? (
                  <p>
                    Every internet-facing system discovered for {domain} runs on a cloud,
                    SaaS or hosting platform. Those providers maintain the underlying
                    infrastructure, so none of it is expected to carry a management agent,
                    and nothing self-hosted was found that falls outside management.
                  </p>
                ) : coverage.unmanaged === 0 ? (
                  <p>
                    Every self-hosted internet-facing system discovered for {domain} matches
                    a device under active management, so patching, monitoring and backup
                    extend to all of it.
                  </p>
                ) : (
                  <>
                    <p>
                      {coverage.unmanaged} of the {coverage.total} self-hosted internet-facing{" "}
                      {coverage.total === 1 ? "system" : "systems"} discovered for {domain}{" "}
                      {coverage.unmanaged === 1 ? "does" : "do"} not match any managed device.
                      A system outside management may not be patched, monitored or backed up,
                      and will not appear in service reporting.
                    </p>
                    <table className="deck-table">
                      <thead><tr><th>Unmanaged system</th><th>Address</th><th>Network</th></tr></thead>
                      <tbody>
                        {coverage.unmanagedHosts.slice(0, 15).map((h, i) => (
                          <tr key={i}>
                            <td><div className="deck-table-title">{h.host}</div></td>
                            <td className="deck-fix-cell">{(h.ips || []).join(", ") || "—"}</td>
                            <td className="deck-fix-cell">{h.network || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {coverage.unmanagedHosts.length > 15 && (
                      <p className="deck-table-desc">and {coverage.unmanagedHosts.length - 15} more.</p>
                    )}
                  </>
                )}

                {coverage.thirdPartyHosts?.length > 0 && (
                  <>
                    <p className="deck-table-desc" style={{ marginTop: coverage.total ? "1rem" : 0 }}>
                      Hosted by third-party providers{coverage.total ? " (not counted above)" : ""}:
                    </p>
                    <table className="deck-table">
                      <thead><tr><th>System</th><th>Provider</th></tr></thead>
                      <tbody>
                        {coverage.thirdPartyHosts.slice(0, 15).map((h, i) => (
                          <tr key={i}>
                            <td><div className="deck-table-title">{h.host}</div></td>
                            <td className="deck-fix-cell">{h.provider || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </section>
          )}

          {/* Per-category findings (all inline) */}
          {categories.map((category, index) => {
            const catFindings = category.findings || [];
            const catPass = catFindings.filter(f => f.status === 'pass').length;
            const catWarn = catFindings.filter(f => f.status === 'warning').length;
            const catFail = catFindings.filter(f => f.status === 'fail').length;
            const g = categoryGrade(category);
            return (
              <section key={index} className="report-section report-category">
                <div className="deck-eyebrow">🚩 CATEGORY {index + 1} / {categoryCount}</div>
                <h2 className="deck-heading">{category.name}</h2>
                <div className="deck-cat-stats">
                  <span className={`deck-grade grade-${g}`}>{g}</span>
                  <span className="deck-cat-chip">{catFindings.length} finding{catFindings.length === 1 ? '' : 's'}</span>
                  {catPass > 0 && <span className="deck-cat-chip chip-pass">✓ {catPass} passed</span>}
                  {catWarn > 0 && <span className="deck-cat-chip chip-warn">⚠ {catWarn} warning{catWarn === 1 ? '' : 's'}</span>}
                  {catFail > 0 && <span className="deck-cat-chip chip-fail">✗ {catFail} failed</span>}
                </div>
                <div className="report-findings">
                  {catFindings.map((finding, fi) => (
                    <div key={fi} className="deck-panel report-finding">
                      <div className="deck-detail-header">
                        {sevPill(finding.severity)}
                        <h3 className="deck-detail-title">{finding.title}</h3>
                      </div>
                      {finding.description && <p className="deck-detail-desc">{finding.description}</p>}
                      <div className="finding-status d-flex flex-wrap gap-2 align-items-center mb-2">
                        <span className={`status-badge status-${finding.status}`}>
                          {finding.status === 'pass' ? '✅ Pass' : finding.status === 'fail' ? '❌ Fail' : finding.status === 'warning' ? '⚠️ Warning' : 'ℹ️ Info'}
                        </span>
                        {finding.value && <span className="finding-value badge bg-light text-dark">Value: {finding.value}</span>}
                        {finding.impact > 0 && <span className="finding-impact badge bg-danger">Impact: {finding.impact}%</span>}
                      </div>
                      {finding.detail && finding.detail.length > 0 && (
                        <div className="report-detail">
                          <div className="deck-panel-label">DETAILED ANALYSIS</div>
                          <div className="detail-text mt-2">{renderDetail(finding.detail)}</div>
                          {finding.compliance && finding.compliance.length > 0 && (
                            <div className="detail-compliance mt-3 pt-2 border-top">
                              <span className="compliance-label d-block mb-2 small fw-bold text-secondary">Compliance Frameworks:</span>
                              <div className="d-flex flex-wrap gap-1">
                                {finding.compliance.map((s, si) => (<span key={si} className="compliance-badge badge bg-info bg-opacity-10 text-info">{s}</span>))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            );
          })}

          {/* Outcome */}
          <section className="report-section">
            <div className="deck-eyebrow">🏆 THE OUTCOME</div>
            <h2 className="deck-heading">Why {domain} Would Benefit <span className="deck-heading-accent">From Fixing This Now</span></h2>
            <div className="deck-outcome-grid">
              <div className="deck-outcome-card"><div className="deck-outcome-num">01</div><h4>Close the Gaps Before Attackers Find Them</h4>
                <p>{criticalCount + highCount > 0
                  ? `${criticalCount + highCount} critical/high finding${criticalCount + highCount === 1 ? '' : 's'} in this report ${criticalCount + highCount === 1 ? 'is' : 'are'} visible to anyone who scans ${domain}. Fixing them removes the easiest attack paths first.`
                  : issueCount > 0
                    ? `No critical gaps today — closing the ${plural(issueCount, 'remaining item')} in this report keeps it that way.`
                    : `No gaps today. Continuous monitoring keeps it that way as new exposures appear.`}</p></div>
              <div className="deck-outcome-card"><div className="deck-outcome-num">02</div><h4>Protect Customers &amp; Reputation</h4>
                <p>Email impersonation and lookalike domains directly target the people who trust you. Closing those vectors protects your brand and every message you send.</p></div>
              <div className="deck-outcome-card"><div className="deck-outcome-num">03</div><h4>Meet Compliance Expectations</h4>
                <p>The findings here map to frameworks like SOC 2, ISO 27001, and OWASP. Remediating them strengthens your posture ahead of audits and customer security reviews.</p></div>
              <div className="deck-outcome-card"><div className="deck-outcome-num">04</div><h4>Sleep Better at Night</h4>
                <p>{providerName
                  ? `With ${providerName} monitoring your external surface, new exposures get caught and remediated before they're weaponized — while you focus on running your business.`
                  : `Continuous monitoring of your external surface catches new exposures before they're weaponized — prevention costs far less than recovery.`}</p></div>
            </div>
          </section>

          {/* Footer */}
          <section className="report-section report-footer-section">
            {leadCollected && onDownload && (
              <button className="download-btn deck-download" onClick={onDownload}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="18" height="18">
                  <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ fontWeight: 700, letterSpacing: '0.5px' }}>DOWNLOAD FULL REPORT</span>
              </button>
            )}
            <ReportLogo provider={provider} size="sm" />
            {provider?.tagline && <p className="deck-tagline">{provider.tagline}</p>}
            <div className="deck-contact">
              {provider?.website && <div className="deck-contact-line">🌍 <a href={provider.website} target="_blank" rel="noreferrer">{provider.website}</a></div>}
              {provider?.phone && <div className="deck-contact-line">📞 <a href={`tel:${provider.phone}`}>{provider.phone}</a></div>}
              {provider?.email && <div className="deck-contact-line">✉️ <a href={`mailto:${provider.email}`}>{provider.email}</a></div>}
              {provider?.address && <div className="deck-contact-line">📍 {provider.address}</div>}
            </div>
            <div className="powered-by deck-powered">
              <span>Powered by</span><strong>Pipsqueak NetScan</strong><span className="version">v{version}</span>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default ReportPage;
